import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import api from '../services/api';
import { useToast } from '../components/shared/ToastNotification';
import TaxIntel from '../components/shared/TaxIntel';

// Premium Invoice Form - Mobile-friendly and elegant
function InvoiceForm() {
    const navigate = useNavigate();
    const { id } = useParams();
    const toast = useToast();
    const isEdit = !!id;

    const [formData, setFormData] = useState({
        customer_id: '',
        order_id: '',
        invoice_date: new Date().toISOString().split('T')[0],
        due_date: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
        status: 'draft',
        notes: '',
        discount_amount: 0,
        location_id: ''
    });

    interface Payment {
        id?: number | string;
        amount: number | string;
        payment_method: string;
        transaction_id: string;
        cheque_number: string;
        _destroy?: boolean;
    }

    const [payments, setPayments] = useState<Payment[]>([{ amount: 0, payment_method: 'cash', transaction_id: '', cheque_number: '', _destroy: false }]);
    const [paymentMode, setPaymentMode] = useState('cash');

    interface InvoiceLineItem {
        id: string | number;
        product_id: string;
        product_unit_id: string | null;
        quantity: number;
        unit_price: number;
        discount_percent: number;
        tax_rate: number;
        tax_rate_id?: string | number;
        mrp: number;
        batch_id: string;
        stock_batch_id: string;
        availableBatches: any[];
        product_variant_id: string;
        availableVariants: any[];
        _destroy: boolean;
        track_batches?: boolean;
        availableUnits?: any[];
        tax_type?: 'auto' | 'cgst_sgst' | 'igst';
        cgst_rate?: number;
        sgst_rate?: number;
        igst_rate?: number;
    }

    const [locations, setLocations] = useState<any[]>([]);
    const [customer, setCustomer] = useState<any>(null);
    const [customerSearch, setCustomerSearch] = useState('');
    const [customerResults, setCustomerResults] = useState<any[]>([]);
    const [showCustomerResults, setShowCustomerResults] = useState(false);
    const [showNewCustomerForm, setShowNewCustomerForm] = useState(false);
    const [newCustomer, setNewCustomer] = useState({ name: '', phone: '', email: '', state: '' });

    const [orders, setOrders] = useState<any[]>([]);
    const [taxRates, setTaxRates] = useState<any[]>([]);

    const [lineItems, setLineItems] = useState<InvoiceLineItem[]>([createEmptyLineItem()]);
    const [products, setProducts] = useState<any[]>([]);
    const [loading, setLoading] = useState(false);
    const [initialLoading, setInitialLoading] = useState(true);
    const [errors, setErrors] = useState<string[]>([]);

    useEffect(() => {
        const init = async () => {
            try {
                setInitialLoading(true);
                await Promise.all([fetchProducts(), fetchLocations(), fetchOrders(), fetchTaxRates()]);
                if (isEdit) {
                    await fetchInvoice();
                }
            } catch (error) {
                console.error('Error initializing InvoiceForm:', error);
                setErrors(['Failed to load form data. Please refresh the page.']);
            } finally {
                setInitialLoading(false);
            }
        };
        init();
    }, [id]);

    const fetchProducts = async () => {
        try {
            const response = await api.get('/products');
            const productsData = Array.isArray(response.data) ? response.data : (response.data?.products || response.data?.data || []);
            setProducts(productsData);
        } catch (error) {
            console.error('Error fetching products:', error);
        }
    };

    const fetchTaxRates = async () => {
        try {
            const response = await api.get('/tax_rates');
            setTaxRates(response.data || response.data?.data || []);
        } catch (error) {
            console.error('Error fetching tax rates:', error);
        }
    };

    const fetchOrders = async () => {
        try {
            const response = await api.get('/orders');
            setOrders(response.data?.data || response.data || []);
        } catch (error) {
            console.error('Error fetching orders:', error);
        }
    };

    const fetchLocations = async () => {
        try {
            const response = await api.get('/locations');
            setLocations(response.data?.data || []);
        } catch (error) {
            console.error('Error fetching locations:', error);
        }
    };

    const fetchInvoice = async () => {
        try {
            const response = await api.get(`/invoices/${id}`);
            const invoice = response.data.data || response.data;
            setFormData({
                customer_id: invoice.customer_id,
                order_id: invoice.order_id || '',
                invoice_date: invoice.invoice_date,
                due_date: invoice.due_date,
                status: invoice.status,
                notes: invoice.notes || '',
                discount_amount: invoice.discount_amount || 0,
                location_id: invoice.location_id || ''
            });
            setCustomer(invoice.customer);

            if (invoice.payments && invoice.payments.length > 0) {
                setPayments(invoice.payments.map((p: any) => ({
                    id: p.id,
                    amount: p.amount,
                    payment_method: p.payment_method || 'cash',
                    transaction_id: p.transaction_id || '',
                    cheque_number: p.cheque_number || '',
                    _destroy: false
                })));
                if (invoice.payments.length > 1) {
                    setPaymentMode('split');
                } else {
                    setPaymentMode(invoice.payments[0].payment_method || 'cash');
                }
            }
            setCustomerSearch(invoice.customer?.name || '');
            if (invoice.items && invoice.items.length > 0) {
                setLineItems(invoice.items.map((item: any) => ({
                    id: item.id,
                    isExistingItem: true, // Mark as existing in THIS invoice
                    product_id: item.product_id,
                    product_unit_id: item.product_unit_id,
                    quantity: parseFloat(item.quantity || 0),
                    unit_price: parseFloat(item.unit_price || 0),
                    discount_percent: parseFloat(item.discount_percent || 0),
                    tax_rate: parseFloat(item.tax_rate || 0),
                    tax_rate_id: item.tax_rate_id,
                    mrp: parseFloat(item.mrp || 0),
                    batch_id: item.batch_id,
                    stock_batch_id: item.stock_batch_id || item.batch_id,
                    product_variant_id: item.product_variant_id,
                    tax_type: item.tax_type || 'auto',
                    cgst_rate: parseFloat(item.cgst_rate || 0),
                    sgst_rate: parseFloat(item.sgst_rate || 0),
                    igst_rate: parseFloat(item.igst_rate || 0),
                    _destroy: false,
                    availableUnits: [] // Initialize default
                })));

                invoice.items.forEach((item: any, index: number) => {
                    if (item.product_id) populateItemDetails(index, item.product_id);
                });
            }
        } catch (error) {
            console.error('Error fetching invoice:', error);
        }
    };

    function createEmptyLineItem(): InvoiceLineItem {
        return {
            id: Date.now() + Math.random(),
            product_id: '',
            product_unit_id: null,
            quantity: 1,
            unit_price: 0,
            discount_percent: 0,
            tax_rate: 0,
            tax_rate_id: '',
            mrp: 0,
            batch_id: '',
            stock_batch_id: '',
            availableBatches: [],
            product_variant_id: '',
            availableVariants: [],
            _destroy: false,
            track_batches: false,
            availableUnits: [],
            tax_type: 'auto',
            cgst_rate: 0,
            sgst_rate: 0,
            igst_rate: 0
        };
    }

    const searchCustomers = async (query: string) => {
        if (query.length < 2) {
            setCustomerResults([]);
            setShowCustomerResults(false);
            return;
        }

        try {
            const response = await api.get(`/search/customers?q=${encodeURIComponent(query)}`);
            setCustomerResults(response.data.customers || []);
            setShowCustomerResults(true);
        } catch (error) {
            console.error('Error searching customers:', error);
        }
    };

    const selectCustomer = (cust: any) => {
        setCustomer(cust);
        setCustomerSearch(cust.name);
        setFormData(prev => ({ ...prev, customer_id: cust.id }));
        setShowCustomerResults(false);
    };

    const clearCustomer = () => {
        setCustomer(null);
        setCustomerSearch('');
        setFormData(prev => ({ ...prev, customer_id: '' }));
    };

    const handleNewCustomerClick = () => {
        setNewCustomer({ name: customerSearch, phone: '', email: '', state: '' });
        setShowNewCustomerForm(true);
        setShowCustomerResults(false);
    };

    const createAndSelectCustomer = async () => {
        if (!newCustomer.name.trim()) {
            setErrors(['Customer name is required']);
            return;
        }

        try {
            const response = await api.post('/search/quick_create_customer', { customer: newCustomer });
            if (response.success) {
                const customerData = response.data?.customer || response.data;
                selectCustomer(customerData);
                setShowNewCustomerForm(false);
                toast.success('Customer created successfully');
            } else {
                const backendErrors = response.errors?.length > 0
                    ? response.errors
                    : [response.message || response.error || 'Error creating customer'];
                setErrors(backendErrors);
                toast.error(backendErrors[0]);
            }
        } catch (error: any) {
            console.error('Error creating customer:', error);
            const msg = error.message || 'Error creating customer';
            setErrors([msg]);
            toast.error(msg);
        }
    };


    const removeLineItem = (index: number) => {
        setLineItems(prev => prev.filter((_, i) => i !== index));
    };

    const updateLineItem = (index: number, field: string, value: any) => {
        setLineItems(prev => prev.map((item, i) =>
            i === index ? { ...item, [field]: value } : item
        ));
    };

    const populateItemDetails = async (index: number, productId: string) => {
        try {
            const res = await api.get(`/products/${productId}`);
            const product = res.data?.product || res.data;

            if (product && product.variants && product.variants.length > 0) {
                updateLineItem(index, 'availableVariants', product.variants);
            } else {
                updateLineItem(index, 'availableVariants', []);
            }

            if (product && product.track_batches) {
                const locationId = formData.location_id || '';
                const batchRes = await api.get(`/products/${productId}/batches?location_id=${locationId}`);
                const validBatches = batchRes.data?.batches || [];
                updateLineItem(index, 'availableBatches', validBatches);
                
                // If there is only one batch, auto-select it
                if (validBatches.length === 1) {
                    updateLineItem(index, 'stock_batch_id', validBatches[0].id);
                }
            }

            if (product && product.serialized) {
                const unitRes = await api.get(`/products/${productId}/units/available`);
                const units = Array.isArray(unitRes.data) ? unitRes.data : (unitRes.data?.data || unitRes.data?.units || []);
                updateLineItem(index, 'availableUnits', units);
            }

            // Set default tax rate from product
            if (product && product.tax_rate_id) {
                updateLineItem(index, 'tax_rate_id', product.tax_rate_id);
                const rate = taxRates.find(r => String(r.id) === String(product.tax_rate_id));
                if (rate) {
                    updateLineItem(index, 'tax_rate', parseFloat(rate.percentage));
                }
            }

        } catch (e) {
            console.error("Error populating item details", e);
        }
    };

    const addPayment = () => {
        setPayments(prev => [...prev, { id: `new-${Date.now()}`, amount: 0, payment_method: 'cash', transaction_id: '', cheque_number: '', _destroy: false }]);
    };

    const removePayment = (index: number) => {
        setPayments(prev => prev.map((p, i) => i === index ? { ...p, _destroy: true } : p));
    };

    const updatePayment = (index: number, field: string, value: any) => {
        setPayments(prev => prev.map((p, i) => i === index ? { ...p, [field]: value } : p));
    };


    const handleOrderSelect = async (orderId: string) => {
        setFormData(prev => ({ ...prev, order_id: orderId }));
        if (!orderId) {
            setLineItems([]);
            return;
        }

        try {
            setLoading(true);
            const res = await api.get(`/orders/${orderId}`);
            const order = res.data.data || res.data;

            // Auto-select customer if not already selected
            if (order.customer_id && !formData.customer_id) {
                setFormData(prev => ({ ...prev, customer_id: order.customer_id }));
                if (order.customer) {
                    setCustomer(order.customer);
                    setCustomerSearch(order.customer.name);
                }
            }

            // Populate items
            if (order.items && order.items.length > 0) {
                setLineItems(order.items.map((item: any) => ({
                    id: Date.now() + Math.random(),
                    isExistingItem: false, // Explicitly false for new invoice items from order
                    product_id: item.product_id,
                    product_unit_id: item.product_unit_id,
                    quantity: parseFloat(item.quantity || 0),
                    unit_price: parseFloat(item.unit_price || 0),
                    discount_percent: parseFloat(item.discount_percent || 0),
                    tax_rate: parseFloat(item.tax_rate || 0),
                    tax_rate_id: item.tax_rate_id,
                    mrp: parseFloat(item.mrp || 0),
                    batch_id: item.batch_id,
                    stock_batch_id: item.stock_batch_id || item.batch_id,
                    product_variant_id: item.product_variant_id,
                    _destroy: false,
                    product_name: item.product?.name || item.product_name || 'Assigned Product',
                    availableUnits: []
                })));
            } else {
                setLineItems([]);
            }
        } catch (e) {
            console.error('Error fetching order details:', e);
        } finally {
            setLoading(false);
        }
    };

    const handlePaymentModeChange = (mode: string) => {
        setPaymentMode(mode);
        if (mode === 'split') {
            // Keep existing payments but ensure at least one is visible
            if (payments.filter(p => !p._destroy).length === 0) {
                addPayment();
            }
        } else {
            // Single payment mode: Reduce to one payment row with the selected method
            const firstActive = payments.find(p => !p._destroy);
            const remainingAmount = calculateTotals().total;
            
            setPayments([
                {
                    amount: firstActive?.amount || remainingAmount,
                    payment_method: mode,
                    transaction_id: firstActive?.transaction_id || '',
                    cheque_number: firstActive?.cheque_number || '',
                    _destroy: false,
                    id: firstActive?.id
                }
            ]);
        }
    };

    const getLineItemTotals = (item: any) => {
        const subtotal = item.quantity * item.unit_price;
        const discountAmount = subtotal * (item.discount_percent / 100);
        const taxableAmount = subtotal - discountAmount;
        const taxAmount = taxableAmount * (item.tax_rate / 100);
        return { taxAmount, total: taxableAmount + taxAmount };
    };

    const calculateTotals = () => {
        const subtotal = lineItems.reduce((sum, item) => sum + (item.quantity * item.unit_price), 0);
        const lineDiscounts = lineItems.reduce((sum, item) => {
            const itemSubtotal = item.quantity * item.unit_price;
            return sum + (itemSubtotal * (item.discount_percent / 100));
        }, 0);
        let taxAmount = 0;
        let totalCgst = 0;
        let totalSgst = 0;
        let totalIgst = 0;

        // @ts-ignore
        const currentFirm = (window as any).CURRENT_FIRM || {};
        const firmState = currentFirm.state || '';
        const clientState = customer?.state || '';
        const isInterState = firmState && clientState && firmState.toLowerCase() !== clientState.toLowerCase();

        lineItems.forEach(item => {
            if (item._destroy) return;
            const itemSubtotal = item.quantity * item.unit_price;
            const discountAmt = itemSubtotal * (item.discount_percent / 100);
            const taxableAmt = itemSubtotal - discountAmt;
            
            let itemTax = 0;
            const selectedRate = taxRates.find(r => String(r.id) === String(item.tax_rate_id));
            const percentage = selectedRate ? parseFloat(selectedRate.percentage) : (item.tax_rate || 0);

            if (item.tax_type === 'cgst_sgst') {
                const cgst = taxableAmt * ((item.cgst_rate || (percentage / 2)) / 100);
                const sgst = taxableAmt * ((item.sgst_rate || (percentage / 2)) / 100);
                itemTax = cgst + sgst;
                totalCgst += cgst;
                totalSgst += sgst;
            } else if (item.tax_type === 'igst') {
                const igst = taxableAmt * ((item.igst_rate || percentage) / 100);
                itemTax = igst;
                totalIgst += igst;
            } else {
                // Auto
                itemTax = taxableAmt * (percentage / 100);
                if (isInterState) {
                    totalIgst += itemTax;
                } else {
                    totalCgst += itemTax / 2;
                    totalSgst += itemTax / 2;
                }
            }
            taxAmount += itemTax;
        });
        const total = subtotal - lineDiscounts + taxAmount - parseFloat(formData.discount_amount.toString() || '0');

        return { subtotal, lineDiscounts, taxAmount, totalCgst, totalSgst, totalIgst, total, isInterState };
    };

    const { subtotal, lineDiscounts, totalCgst, totalSgst, totalIgst, total, isInterState } = calculateTotals();

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!formData.customer_id) {
            setErrors(['Please select a customer']);
            window.scrollTo({ top: 0, behavior: 'smooth' });
            return;
        }

        const validLineItems = lineItems.filter(item => item.product_id && !item._destroy);
        if (validLineItems.length === 0) {
            setErrors(['Please add at least one product']);
            return;
        }

        const invoiceData = {
            ...formData,
            invoice_items_attributes: lineItems.map(item => ({
                // Only send ID if it's an existing invoice item (to update/delete)
                // New items (even from Order) should NOT have an ID in the payload for a new Invoice
                id: (isEdit && (item as any).isExistingItem) ? item.id : undefined,
                product_id: item.product_id,
                product_unit_id: item.product_unit_id,
                quantity: item.quantity,
                unit_price: item.unit_price,
                mrp: item.mrp,
                discount_percent: item.discount_percent,
                tax_rate: item.tax_rate,
                tax_rate_id: item.tax_rate_id,
                batch_id: item.batch_id,
                stock_batch_id: item.stock_batch_id || item.batch_id, // Fallback for transition
                product_variant_id: item.product_variant_id,
                _destroy: item._destroy
            })),
            payments_attributes: payments.filter(p => !p._destroy || p.id).map(p => ({
                id: (typeof p.id === 'string' && (p.id as string).includes('-') || !p.id) ? undefined : p.id,
                amount: p.amount || 0,
                payment_method: p.payment_method,
                transaction_id: p.transaction_id,
                cheque_number: p.cheque_number,
                _destroy: p._destroy
            }))
        };

        setLoading(true);
        try {
            let response;
            if (isEdit) {
                response = await api.put(`/invoices/${id}`, { invoice: invoiceData });
            } else {
                response = await api.post('/invoices', { invoice: invoiceData });
            }

            if (response.success) {
                const invoiceId = response.data?.id || response.data?.data?.id || response.id;
                toast.success(`Invoice ${isEdit ? 'updated' : 'created'} successfully`);
                navigate(`/invoices/${invoiceId}`);
            } else {
                const backendErrors = response.errors?.length > 0
                    ? response.errors
                    : [response.message || response.error || 'Failed to save invoice'];
                
                setErrors(backendErrors);
                toast.error(backendErrors[0]);
                window.scrollTo({ top: 0, behavior: 'smooth' });
            }
        } catch (error: any) {
            console.error('Error saving invoice:', error);
            const msg = error.message || 'An unexpected error occurred';
            setErrors([msg]);
            toast.error(msg);
            window.scrollTo({ top: 0, behavior: 'smooth' });
        } finally {
            setLoading(false);
        }
    };

    if (initialLoading) {
        return (
            <div className="min-h-[60vh] flex flex-col items-center justify-center">
                <div className="w-12 h-12 border-4 border-indigo-500/30 border-t-indigo-600 rounded-full animate-spin mb-4"></div>
                <div className="text-slate-500 font-medium">Loading form details...</div>
            </div>
        );
    }

    return (
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8 animate-in fade-in slide-in-from-bottom-4 duration-700">
            {/* Header */}
            <div className="flex items-center justify-between mb-8">
                <div>
                    <h1 className="text-2xl sm:text-4xl font-black text-slate-900 tracking-tight">
                        {isEdit ? 'Edit' : 'Create'} <span className="text-indigo-600">Invoice</span>
                    </h1>
                    <p className="text-slate-500 mt-1 font-medium italic">Standard document for your sales transactions</p>
                </div>
                <Link to="/invoices" className="p-2 hover:bg-slate-100 rounded-full transition-colors text-slate-400">
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                    </svg>
                </Link>
            </div>

            {errors.length > 0 && (
                <div className="bg-rose-50 border-l-4 border-rose-500 rounded-2xl p-4 text-rose-800 text-sm mb-8 shadow-sm flex items-start gap-3">
                    <svg className="w-5 h-5 shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                    </svg>
                    <ul className="list-disc list-inside font-medium italic">
                        {errors.map((error, i) => <li key={i}>{error}</li>)}
                    </ul>
                </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-8 pb-32">
                {/* Section 1: Customer & Basics */}
                <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-6">
                    <div className="flex items-center gap-3 mb-8">
                        <div className="w-10 h-10 bg-indigo-50 text-indigo-600 rounded-xl flex items-center justify-center font-black">1</div>
                        <h2 className="text-xs font-black text-slate-400 uppercase tracking-[0.2em]">Customer & Basics</h2>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        {/* Customer Selection */}
                        <div className="space-y-4">
                            <label className="block text-sm font-black text-slate-700 uppercase tracking-wider">
                                Select Customer <span className="text-rose-500">*</span>
                            </label>

                            <div className="relative">
                                {!customer ? (
                                    <div className="relative">
                                        <input
                                            type="text"
                                            placeholder="Type name or phone..."
                                            autoComplete="off"
                                            className="w-full px-5 py-4 pl-12 bg-slate-50 border border-slate-200 rounded-2xl focus:ring-4 focus:ring-indigo-500/10 focus:border-indigo-500 outline-none transition-all font-medium"
                                            value={customerSearch}
                                            onChange={(e) => {
                                                setCustomerSearch(e.target.value);
                                                searchCustomers(e.target.value);
                                            }}
                                        />
                                        <svg className="w-5 h-5 absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                                        </svg>

                                        {showCustomerResults && (
                                            <div className="absolute z-50 w-full mt-2 bg-white border border-slate-200 rounded-2xl shadow-2xl max-h-60 overflow-y-auto p-2">
                                                {customerResults.length > 0 ? (
                                                    customerResults.map(cust => (
                                                        <button
                                                            key={cust.id}
                                                            type="button"
                                                            onClick={() => selectCustomer(cust)}
                                                            className="w-full text-left px-4 py-3 hover:bg-indigo-50 rounded-xl flex items-center justify-between group transition-colors"
                                                        >
                                                            <div>
                                                                <div className="font-black text-slate-900 group-hover:text-indigo-700">{cust.name}</div>
                                                                <div className="text-xs text-slate-500 font-bold">{cust.phone || cust.email || 'No contact info'}</div>
                                                            </div>
                                                            <svg className="w-4 h-4 text-slate-300 group-hover:text-indigo-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
                                                            </svg>
                                                        </button>
                                                    ))
                                                ) : (
                                                    <div className="p-4 text-center">
                                                        <p className="text-slate-500 text-sm font-medium mb-3">No matching customers</p>
                                                        <button
                                                            type="button"
                                                            onClick={handleNewCustomerClick}
                                                            className="w-full py-2 bg-indigo-600 text-white rounded-xl text-xs font-black uppercase tracking-widest hover:bg-indigo-700 transition-colors"
                                                        >
                                                            + Create "{customerSearch}"
                                                        </button>
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                ) : (
                                    <div className="flex items-center justify-between p-5 bg-indigo-50/50 border border-indigo-100 rounded-[1.5rem] animate-in zoom-in-95 duration-300">
                                        <div className="flex items-center gap-4">
                                            <div className="w-12 h-12 bg-white rounded-2xl flex items-center justify-center text-indigo-600 font-black shadow-sm ring-1 ring-indigo-100">
                                                {customer.name.charAt(0)}
                                            </div>
                                            <div>
                                                <p className="font-black text-slate-900">{customer.name}</p>
                                                <p className="text-xs font-bold text-slate-500 uppercase tracking-tighter">{customer.phone || customer.email || 'Standard Customer'}</p>
                                            </div>
                                        </div>
                                        <button
                                            type="button"
                                            onClick={clearCustomer}
                                            className="p-2 text-rose-500 hover:bg-rose-50 rounded-xl transition-colors"
                                        >
                                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                            </svg>
                                        </button>
                                    </div>
                                )}
                            </div>

                            {/* New Customer Inline Form */}
                            {showNewCustomerForm && (
                                <div className="p-6 bg-slate-50 border border-indigo-100 rounded-3xl space-y-4 animate-in slide-in-from-top-2 duration-300 shadow-inner shadow-slate-200/50">
                                    <h4 className="text-xs font-black text-indigo-600 uppercase tracking-widest flex items-center gap-2">
                                        <div className="w-2 h-2 bg-indigo-500 rounded-full"></div>
                                        Quick Setup
                                    </h4>
                                    <div className="grid grid-cols-1 gap-4">
                                        <input
                                            type="text"
                                            value={newCustomer.name}
                                            onChange={(e) => setNewCustomer(prev => ({ ...prev, name: e.target.value }))}
                                            className="w-full px-4 py-3 bg-white border border-slate-200 rounded-xl outline-none focus:border-indigo-500 transition-all font-medium text-sm"
                                            placeholder="Legal Name"
                                        />
                                        <div className="grid grid-cols-2 gap-4">
                                            <input
                                                type="tel"
                                                value={newCustomer.phone}
                                                onChange={(e) => setNewCustomer(prev => ({ ...prev, phone: e.target.value }))}
                                                className="w-full px-4 py-3 bg-white border border-slate-200 rounded-xl outline-none focus:border-indigo-500 transition-all font-medium text-sm"
                                                placeholder="Mobile Number"
                                            />
                                            <input
                                                type="email"
                                                value={newCustomer.email}
                                                onChange={(e) => setNewCustomer(prev => ({ ...prev, email: e.target.value }))}
                                                className="w-full px-4 py-3 bg-white border border-slate-200 rounded-xl outline-none focus:border-indigo-500 transition-all font-medium text-sm"
                                                placeholder="Email Address"
                                            />
                                        </div>
                                    </div>
                                    <div className="flex gap-2">
                                        <button
                                            type="button"
                                            onClick={createAndSelectCustomer}
                                            className="flex-1 py-3 bg-indigo-600 text-white rounded-xl text-xs font-black uppercase tracking-widest hover:bg-indigo-700 transition-colors shadow-lg shadow-indigo-200"
                                        >
                                            Confirm
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => setShowNewCustomerForm(false)}
                                            className="px-6 py-3 bg-white border border-slate-200 text-slate-500 rounded-xl text-xs font-black uppercase tracking-widest hover:bg-slate-50 transition-colors"
                                        >
                                            Esc
                                        </button>
                                    </div>
                                </div>
                            )}

                            {/* Order Selection */}
                            <div className="pt-6 mt-6 border-t border-slate-100">
                                <label className="block text-sm font-black text-slate-700 uppercase tracking-wider mb-4">
                                    Source Order Reference
                                </label>
                                <select
                                    value={formData.order_id || ''}
                                    onChange={(e) => handleOrderSelect(e.target.value)}
                                    className="w-full px-6 py-4 bg-slate-50 border border-slate-200 rounded-2xl focus:ring-4 focus:ring-indigo-500/10 focus:border-indigo-500 outline-none transition-all font-bold text-slate-700 appearance-none cursor-pointer"
                                >
                                    <option value="">Select an Order...</option>
                                    {orders.filter(o => !customer || o.customer_id === customer.id).map(o => (
                                        <option key={o.id} value={o.id}>
                                            {o.order_number} - ₹{parseFloat(o.total_amount || 0).toLocaleString()}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        </div>

                        {/* Dates & Location */}
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                            <div className="space-y-4">
                                <label className="block text-sm font-black text-slate-700 uppercase tracking-wider">Dates</label>
                                <div className="space-y-3">
                                    <div className="relative">
                                        <div className="absolute left-4 top-1/2 -translate-y-1/2 text-[10px] font-black text-slate-400 uppercase tracking-tighter">Issue</div>
                                        <input
                                            type="date"
                                            value={formData.invoice_date}
                                            onChange={(e) => setFormData(prev => ({ ...prev, invoice_date: e.target.value }))}
                                            className="w-full pl-16 pr-4 py-3 bg-slate-50 border border-slate-100 rounded-xl focus:ring-4 focus:ring-indigo-500/10 focus:border-indigo-500 outline-none transition-all font-bold text-slate-700"
                                        />
                                    </div>
                                    <div className="relative">
                                        <div className="absolute left-4 top-1/2 -translate-y-1/2 text-[10px] font-black text-slate-400 uppercase tracking-tighter">Due</div>
                                        <input
                                            type="date"
                                            value={formData.due_date}
                                            onChange={(e) => setFormData(prev => ({ ...prev, due_date: e.target.value }))}
                                            className="w-full pl-16 pr-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:ring-4 focus:ring-indigo-500/10 focus:border-indigo-500 outline-none transition-all font-bold text-slate-700"
                                        />
                                    </div>
                                </div>
                            </div>

                            <div className="space-y-4">
                                <label className="block text-sm font-black text-slate-700 uppercase tracking-wider">Warehouse Location</label>
                                <div className="space-y-3">
                                    <select
                                        value={formData.location_id}
                                        onChange={(e) => setFormData(prev => ({ ...prev, location_id: e.target.value }))}
                                        className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:ring-4 focus:ring-indigo-500/10 focus:border-indigo-500 outline-none transition-all font-bold text-slate-700 appearance-none cursor-pointer"
                                    >
                                        <option value="">Select Warehouse</option>
                                        {locations.map(loc => (
                                            <option key={loc.id} value={loc.id}>{loc.name}</option>
                                        ))}
                                    </select>
                                    {/* Payment Section - Removed older fields and appended split payments */}
                                    <div className="pt-2">
                                        <div className="flex justify-between items-center mb-3">
                                            <label className="block text-xs font-black text-slate-400 uppercase tracking-[0.2em]">Payment Mode</label>
                                            {paymentMode === 'split' && (
                                                <button type="button" onClick={addPayment} className="text-[10px] font-black uppercase text-indigo-600 tracking-widest hover:text-indigo-800 transition-colors">
                                                    + Add Split
                                                </button>
                                            )}
                                        </div>
                                        
                                        <div className="mb-4">
                                            <select
                                                value={paymentMode}
                                                onChange={(e) => handlePaymentModeChange(e.target.value)}
                                                className="w-full px-4 py-3 bg-white border-2 border-indigo-100 rounded-xl focus:ring-4 focus:ring-indigo-500/10 focus:border-indigo-500 outline-none transition-all font-black text-slate-900 appearance-none cursor-pointer shadow-sm"
                                            >
                                                <option value="cash">💵 Cash Payment</option>
                                                <option value="upi">📲 UPI / QR Code</option>
                                                <option value="debit_card">💳 Debit Card</option>
                                                <option value="credit_card">💳 Credit Card</option>
                                                <option value="bank_transfer">🏦 Bank Transfer / NEFT</option>
                                                <option value="split">✨ Multi-Payment / Split</option>
                                            </select>
                                        </div>

                                        <div className="space-y-3">
                                            {payments.map((payment, pIndex) => !payment._destroy && (
                                                <div key={payment.id || pIndex} className="bg-slate-50 border border-slate-200 rounded-xl p-3 relative group animate-in slide-in-from-top-2 duration-300">
                                                    <div className="grid grid-cols-1 gap-2">
                                                        <div className="flex gap-2">
                                                            <div className="relative flex-1">
                                                                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 font-bold text-sm">₹</span>
                                                                <input
                                                                    type="number"
                                                                    placeholder="0.00"
                                                                    value={payment.amount}
                                                                    onChange={(e) => updatePayment(pIndex, 'amount', e.target.value)}
                                                                    className="w-full pl-7 pr-3 py-2 bg-white border border-slate-200 rounded-lg outline-none focus:ring-2 focus:ring-indigo-500/20 font-black text-slate-900 transition-all text-sm"
                                                                />
                                                            </div>
                                                            {paymentMode === 'split' ? (
                                                                <select
                                                                    value={payment.payment_method}
                                                                    onChange={(e) => updatePayment(pIndex, 'payment_method', e.target.value)}
                                                                    className="flex-1 px-3 py-2 bg-white border border-slate-200 rounded-lg outline-none focus:ring-2 focus:ring-indigo-500/20 transition-all font-bold text-slate-700 appearance-none cursor-pointer text-sm"
                                                                >
                                                                    <option value="cash">💵 Cash</option>
                                                                    <option value="upi">📲 UPI</option>
                                                                    <option value="debit_card">💳 Debit</option>
                                                                    <option value="credit_card">💳 Credit</option>
                                                                    <option value="bank_transfer">🏦 Bank</option>
                                                                </select>
                                                            ) : (
                                                                <div className="flex-1 px-3 py-2 bg-indigo-50 border border-indigo-100 rounded-lg text-xs font-black text-indigo-600 flex items-center justify-center uppercase tracking-tighter">
                                                                    {paymentMode.replace('_', ' ')}
                                                                </div>
                                                            )}
                                                            {paymentMode === 'split' && (
                                                                <button
                                                                    type="button"
                                                                    onClick={() => removePayment(pIndex)}
                                                                    className="w-9 h-9 bg-white text-rose-500 rounded-lg flex items-center justify-center hover:bg-rose-50 hover:text-rose-600 transition-colors border border-slate-200 shrink-0"
                                                                >
                                                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                                                                </button>
                                                            )}
                                                        </div>
                                                        <div className="flex gap-2">
                                                            {(['upi', 'bank_transfer', 'bank_deposit', 'credit_card', 'debit_card'].includes(paymentMode === 'split' ? payment.payment_method : paymentMode)) && (
                                                                <input
                                                                    type="text"
                                                                    placeholder="Ref / Txn ID"
                                                                    value={payment.transaction_id}
                                                                    onChange={(e) => updatePayment(pIndex, 'transaction_id', e.target.value)}
                                                                    className="w-full px-3 py-2 bg-white border border-slate-200 rounded-lg outline-none focus:ring-2 focus:ring-indigo-500/20 transition-all font-bold text-slate-700 text-sm animate-in slide-in-from-right-2"
                                                                />
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>

                                    <select
                                        value={formData.status}
                                        onChange={(e) => setFormData(prev => ({ ...prev, status: e.target.value }))}
                                        className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:ring-4 focus:ring-indigo-500/10 focus:border-indigo-500 outline-none transition-all font-bold text-slate-700 appearance-none cursor-pointer"
                                    >
                                        <option value="draft">📄 Draft Invoice</option>
                                        <option value="sent">📧 Sent to Client</option>
                                        <option value="paid">💰 Fully Paid</option>
                                        <option value="partially_paid">🌓 Partially Paid</option>
                                        <option value="overdue">⚠️ Overdue</option>
                                        <option value="cancelled">🚫 Cancelled</option>
                                    </select>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Section 2: Line Items */}
                <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-6">
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-6 mb-10">
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-emerald-50 text-emerald-600 rounded-xl flex items-center justify-center font-black">2</div>
                            <h2 className="text-xs font-black text-slate-400 uppercase tracking-[0.2em]">Invoice Items (From Order)</h2>
                        </div>
                    </div>

                    <div className="space-y-6">
                        {lineItems.filter(item => !item._destroy).map((item, index) => (
                            <div key={item.id} className="p-6 bg-white border border-slate-200 rounded-xl shadow-sm relative">
                                <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center relative z-10">
                                    {/* Column 1: Product & Batch Selection */}
                                    <div className="lg:col-span-4 space-y-4">
                                        <div className="flex items-center gap-2 mb-1">
                                            <div className="w-5 h-5 bg-slate-100 rounded-lg flex items-center justify-center">
                                                <svg className="w-3 h-3 text-slate-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" /></svg>
                                            </div>
                                            <label className="text-[10px] font-black text-slate-400 uppercase tracking-[0.2em]">Product Assignment</label>
                                        </div>
                                        <div className="w-full px-6 py-4 bg-slate-50 border border-slate-100 rounded-2xl font-black text-slate-700 shadow-sm opacity-80 cursor-not-allowed">
                                            {/* @ts-ignore */}
                                            {item.product_name || products.find(p => String(p.id) === String(item.product_id))?.name || 'Assigned Product'}
                                        </div>

                                        <div className="grid grid-cols-2 gap-3">
                                            {item.availableVariants && item.availableVariants.length > 0 && (
                                                <div className="relative">
                                                    <select
                                                        value={item.product_variant_id || ''}
                                                        onChange={(e) => {
                                                            const variantId = e.target.value;
                                                            updateLineItem(index, 'product_variant_id', variantId);
                                                            const variant = item.availableVariants.find((v: any) => String(v.id) === String(variantId));
                                                            if (variant && variant.price) updateLineItem(index, 'unit_price', parseFloat(variant.price));
                                                        }}
                                                        className="w-full pl-3 pr-8 py-2.5 bg-indigo-50/30 border border-indigo-100/50 rounded-xl text-[10px] font-black text-indigo-600 outline-none appearance-none cursor-pointer hover:bg-indigo-50 transition-colors"
                                                    >
                                                        <option value="">Any Variant</option>
                                                        {item.availableVariants.map((v: any) => (
                                                            <option key={v.id} value={v.id}>{v.options.map((o: any) => o.presentation).join('/')}</option>
                                                        ))}
                                                    </select>
                                                    <div className="absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none text-indigo-300">
                                                        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M19 9l-7 7-7-7" /></svg>
                                                    </div>
                                                </div>
                                            )}
                                            {item.track_batches && (
                                                <div className="relative">
                                                    <select
                                                        value={item.stock_batch_id || item.batch_id || ''}
                                                        onChange={(e) => {
                                                            const stockBatchId = e.target.value;
                                                            updateLineItem(index, 'stock_batch_id', stockBatchId);
                                                            if (stockBatchId) {
                                                                const selectedBatch = item.availableBatches?.find((b: any) => String(b.id) === String(stockBatchId));
                                                                if (selectedBatch && selectedBatch.tax_rate_id) {
                                                                    updateLineItem(index, 'tax_rate_id', selectedBatch.tax_rate_id);
                                                                    const rate = taxRates.find(r => String(r.id) === String(selectedBatch.tax_rate_id));
                                                                    if (rate) {
                                                                        updateLineItem(index, 'tax_rate', parseFloat(rate.percentage));
                                                                    }
                                                                }
                                                                if (selectedBatch && selectedBatch.mrp) {
                                                                    updateLineItem(index, 'mrp', parseFloat(selectedBatch.mrp));
                                                                }
                                                            }
                                                        }}
                                                        className="w-full pl-3 pr-8 py-2.5 bg-emerald-50/30 border border-emerald-100/50 rounded-xl text-[10px] font-black text-emerald-600 outline-none appearance-none cursor-pointer hover:bg-emerald-50 transition-colors"
                                                    >
                                                        <option value="">Select Batch</option>
                                                        {item.availableBatches?.map((b: any) => (b.stock > 0 || b.id === item.stock_batch_id) && (
                                                            <option key={b.id} value={b.id}>{b.batch_number} ({b.stock}) {b.tax_rate_name ? `[${b.tax_rate_name}]` : ''}</option>
                                                        ))}
                                                    </select>
                                                    <div className="absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none text-emerald-300">
                                                        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M19 9l-7 7-7-7" /></svg>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    {/* Column 2: Inputs Grid */}
                                    <div className="lg:col-span-5">
                                        <div className="grid grid-cols-5 gap-4 bg-slate-50/50 p-6 rounded-[2rem] border border-slate-100/50">
                                            <div className="flex flex-col items-center gap-3">
                                                <label className="text-[9px] font-black text-slate-400 opacity-70 uppercase tracking-widest">Qty</label>
                                                <input
                                                    type="number"
                                                    value={item.quantity}
                                                    onChange={(e) => updateLineItem(index, 'quantity', parseFloat(e.target.value) || 0)}
                                                    className="w-full bg-white border border-slate-200 rounded-2xl py-3 px-2 text-center font-black text-slate-900 shadow-sm focus:ring-4 focus:ring-indigo-500/10 focus:border-indigo-500 outline-none transition-all"
                                                />
                                            </div>
                                            <div className="flex flex-col items-center gap-3">
                                                <label className="text-[9px] font-black text-emerald-400 uppercase tracking-widest text-center leading-tight">MRP</label>
                                                <div className="w-full bg-slate-100 border border-slate-200 rounded-2xl py-3 px-2 text-center font-black text-emerald-600/60 shadow-inner text-xs">
                                                    ₹{item.mrp?.toLocaleString()}
                                                </div>
                                            </div>
                                            <div className="flex flex-col items-center gap-3">
                                                <label className="text-[9px] font-black text-slate-400 opacity-70 uppercase tracking-widest text-center leading-tight">Selling<br/>Price</label>
                                                <input
                                                    type="number"
                                                    value={item.unit_price}
                                                    onChange={(e) => updateLineItem(index, 'unit_price', parseFloat(e.target.value) || 0)}
                                                    className="w-full bg-white border border-slate-200 rounded-2xl py-3 px-2 text-center font-black text-slate-900 shadow-sm focus:ring-4 focus:ring-indigo-500/10 focus:border-indigo-500 outline-none transition-all"
                                                />
                                            </div>
                                            <div className="flex flex-col items-center gap-3">
                                                <label className="text-[9px] font-black text-indigo-400 uppercase tracking-widest">Disc %</label>
                                                <div className="relative w-full">
                                                    <input
                                                        type="number"
                                                        value={item.discount_percent}
                                                        onChange={(e) => updateLineItem(index, 'discount_percent', parseFloat(e.target.value) || 0)}
                                                        className="w-full bg-white border border-slate-200 rounded-2xl py-3 px-2 text-center font-black text-indigo-600 shadow-sm focus:ring-4 focus:ring-indigo-500/10 focus:border-indigo-500 outline-none transition-all"
                                                    />
                                                </div>
                                            </div>
                                            <div className="flex flex-col items-center gap-3">
                                                <label className="text-[9px] font-black text-emerald-400 uppercase tracking-widest text-center leading-tight">GST<br/>Slot</label>
                                                <div className="relative w-full">
                                                    <select
                                                        value={item.tax_rate_id || ''}
                                                        onChange={(e) => {
                                                            const rateId = e.target.value;
                                                            updateLineItem(index, 'tax_rate_id', rateId);
                                                            const rate = taxRates.find(r => String(r.id) === String(rateId));
                                                            if (rate) {
                                                                updateLineItem(index, 'tax_rate', parseFloat(rate.percentage));
                                                            }
                                                        }}
                                                        className="w-full bg-white border border-slate-200 rounded-2xl py-3 px-1 text-center font-black text-emerald-600 shadow-sm focus:ring-4 focus:ring-emerald-500/10 focus:border-emerald-500 outline-none transition-all appearance-none cursor-pointer text-[10px]"
                                                    >
                                                        <option value="">Select</option>
                                                        {taxRates.map(tr => (
                                                            <option key={tr.id} value={tr.id}>{tr.name}</option>
                                                        ))}
                                                    </select>
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Column 3: Row Total */}
                                    <div className="lg:col-span-3">
                                        <div className="flex flex-col items-end">
                                            <div className="flex items-center gap-2 mb-3">
                                                <label className="text-[10px] font-black text-slate-400 uppercase tracking-[0.2em]">Item Total</label>
                                                <button
                                                    type="button"
                                                    onClick={() => removeLineItem(index)}
                                                    className="p-1 text-rose-300 hover:text-rose-500 transition-colors"
                                                >
                                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                                                </button>
                                            </div>
                                            <div className="text-3xl font-black text-slate-900 tracking-tighter">
                                                ₹{getLineItemTotals(item).total.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                            </div>
                                            <div className="text-[10px] font-bold text-slate-400 mt-1 uppercase tracking-widest flex items-center gap-2">
                                                <span>Tax: ₹{getLineItemTotals(item).taxAmount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                                                <div className="w-1 h-1 bg-slate-300 rounded-full"></div>
                                                <span className="text-emerald-500">{item.tax_rate}% GST</span>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Custom Tax Intel for this row */}
                                <div className="mt-6 pt-6 border-t border-slate-50">
                                    <TaxIntel
                                        subtotal={(item.quantity * item.unit_price) * (1 - (item.discount_percent / 100))}
                                        taxRate={item.tax_rate}
                                        isInterState={isInterState}
                                        compact
                                    />
                                </div>
                            </div>
                        ))}

                        {lineItems.filter(item => !item._destroy).length === 0 && (
                            <div className="text-center py-16 bg-slate-50 rounded-lg border border-dashed border-slate-200">
                                <p className="text-slate-400 font-bold italic">Cart is currently empty. Start adding some products.</p>
                            </div>
                        )}
                    </div>
                </div>

                {/* Section 3: Summary & Submit */}
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-start">
                    <div className="lg:col-span-7 bg-white border border-slate-200 rounded-xl shadow-sm p-6">
                        <label className="block text-xs font-black text-slate-400 uppercase tracking-[0.2em] mb-4">Additional Remarks</label>
                        <textarea
                            rows={4}
                            value={formData.notes}
                            onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                            className="w-full px-6 py-4 bg-slate-50 border border-slate-100 rounded-3xl outline-none focus:bg-white focus:border-indigo-500 transition-all font-medium resize-none"
                            placeholder="Terms, payment instructions, or personal note to customer..."
                        ></textarea>
                    </div>

                    <div className="lg:col-span-5 bg-white border border-slate-200 rounded-xl shadow-sm p-6 text-slate-900 space-y-6 lg:-mt-10 lg:sticky lg:top-8">
                        <div className="flex justify-between items-center text-sm font-bold text-slate-500 uppercase tracking-widest">
                            <span>Subtotal</span>
                            <span>₹{subtotal.toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
                        </div>
                        {isInterState ? (
                            <div className="flex justify-between items-center text-sm font-bold text-slate-600 uppercase tracking-widest">
                                <span>IGST</span>
                                <span>+ ₹{totalIgst.toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
                            </div>
                        ) : (
                            <>
                                <div className="flex justify-between items-center text-sm font-bold text-slate-600 uppercase tracking-widest">
                                    <span>CGST</span>
                                    <span>+ ₹{totalCgst.toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
                                </div>
                                <div className="flex justify-between items-center text-sm font-bold text-slate-600 uppercase tracking-widest">
                                    <span>SGST</span>
                                    <span>+ ₹{totalSgst.toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
                                </div>
                            </>
                        )}
                        <div className="flex justify-between items-center text-sm font-bold text-rose-500 uppercase tracking-widest italic">
                            <span>Discounts</span>
                            <span>- ₹{(lineDiscounts + parseFloat(formData.discount_amount.toString() || '0')).toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
                        </div>

                        <div className="pt-6 border-t border-indigo-500/30">
                            <label className="block text-[10px] font-black text-slate-500 uppercase tracking-widest mb-3">Overall Adjustment (₹)</label>
                            <input
                                type="number"
                                value={formData.discount_amount}
                                onChange={(e) => setFormData(prev => ({ ...prev, discount_amount: parseFloat(e.target.value) || 0 }))}
                                className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl outline-none focus:ring-2 focus:ring-indigo-500/20 font-black text-xl text-slate-900"
                            />
                        </div>

                        <div className="pt-6 border-t border-indigo-500/30 flex flex-col gap-3">
                            <div className="flex justify-between items-center text-sm font-bold text-slate-600 uppercase tracking-widest">
                                <span>Grand Total</span>
                                <span>₹{total.toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
                            </div>
                            <div className="flex justify-between items-center text-sm font-bold text-indigo-600 uppercase tracking-widest border-b border-indigo-500/30 pb-3">
                                <span>Total Paid</span>
                                <span>₹{payments.filter(p => !p._destroy).reduce((sum, p) => sum + (parseFloat(p.amount.toString()) || 0), 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
                            </div>
                            <div className="flex justify-between items-end mt-2">
                                <span className="text-[10px] font-black text-slate-500 uppercase tracking-widest mb-1">Balance Due</span>
                                <span className={`text-5xl font-black tracking-tighter ${Math.max(0, total - payments.filter(p => !p._destroy).reduce((sum, p) => sum + (parseFloat(p.amount.toString()) || 0), 0)) > 0 ? 'text-rose-500' : 'text-emerald-500'}`}>
                                    ₹{Math.max(0, total - payments.filter(p => !p._destroy).reduce((sum, p) => sum + (parseFloat(p.amount.toString()) || 0), 0)).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                                </span>
                            </div>
                        </div>

                        <button
                            type="submit"
                            disabled={loading}
                            className={`w-full py-4 rounded-xl font-bold uppercase tracking-widest text-xs transition-all ${loading ? "bg-indigo-300 cursor-not-allowed text-white" : "bg-indigo-600 text-white hover:bg-indigo-700 shadow-sm"}`}
                        >
                            {loading ? 'Finalizing Invoice...' : (isEdit ? 'Update Invoice' : 'Issue Invoice')}
                        </button>
                    </div>
                </div>
            </form>
        </div>
    );
}


function TaxIntel({ subtotal, taxRate, isInterState, compact = false }: { subtotal: number, taxRate: number, isInterState: boolean, compact?: boolean }) {
    const taxAmount = subtotal * (taxRate / 100);
    const total = subtotal + taxAmount;

    if (compact) {
        return (
            <div className="flex items-center gap-4 text-[10px] font-black uppercase tracking-widest text-slate-400">
                <div className="flex items-center gap-2">
                    <div className="w-1.5 h-1.5 bg-indigo-500 rounded-full"></div>
                    <span>Taxable: ₹{subtotal.toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
                </div>
                {isInterState ? (
                    <div className="flex items-center gap-2">
                        <div className="w-1.5 h-1.5 bg-emerald-500 rounded-full"></div>
                        <span>IGST: ₹{taxAmount.toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
                    </div>
                ) : (
                    <>
                        <div className="flex items-center gap-2">
                            <div className="w-1.5 h-1.5 bg-emerald-500 rounded-full"></div>
                            <span>CGST: ₹{(taxAmount / 2).toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
                        </div>
                        <div className="flex items-center gap-2">
                            <div className="w-1.5 h-1.5 bg-emerald-500 rounded-full"></div>
                            <span>SGST: ₹{(taxAmount / 2).toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
                        </div>
                    </>
                )}
            </div>
        );
    }

    return (
        <div className="grid grid-cols-3 gap-4">
            <div className="p-3 bg-slate-50 rounded-xl border border-slate-100">
                <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">Taxable Amount</p>
                <p className="text-lg font-black text-slate-700">₹{subtotal.toLocaleString()}</p>
            </div>
            {isInterState ? (
                <div className="p-3 bg-emerald-50 rounded-xl border border-emerald-100 col-span-2">
                    <p className="text-[10px] font-black text-emerald-600 uppercase tracking-widest mb-1">Integrated Tax (IGST @{taxRate}%)</p>
                    <p className="text-lg font-black text-emerald-700">₹{taxAmount.toLocaleString()}</p>
                </div>
            ) : (
                <>
                    <div className="p-3 bg-indigo-50 rounded-xl border border-indigo-100">
                        <p className="text-[10px] font-black text-indigo-600 uppercase tracking-widest mb-1">Central Tax (CGST @{taxRate / 2}%)</p>
                        <p className="text-lg font-black text-indigo-700">₹{(taxAmount / 2).toLocaleString()}</p>
                    </div>
                    <div className="p-3 bg-amber-50 rounded-xl border border-amber-100">
                        <p className="text-[10px] font-black text-amber-600 uppercase tracking-widest mb-1">State Tax (SGST @{taxRate / 2}%)</p>
                        <p className="text-lg font-black text-amber-700">₹{(taxAmount / 2).toLocaleString()}</p>
                    </div>
                </>
            )}
        </div>
    );
}

export default InvoiceForm;
